import { handleActions } from 'redux-actions';

import type {
  Meta,
  TestResult,
} from 'components/settings/components/sections/cards/cards.types';

import { getParameterFromString } from '../../../../app/utils/getParameter';
import { salesforceActions } from '../../actions/salesforceCards';

export const {
  salesforceGetCardById,
  salesforceGetCardByIdSucceeded,
  salesforceGetCardByIdFailed,

  salesforceGetCards,
  salesforceGetCardsSucceeded,
  salesforceGetCardsFailed,

  salesforceGetCard,

  salesforceTestCard,
  salesforceTestCardSucceeded,
  salesforceTestCardFailed,

  salesforceEditCard,
  salesforceEditCardSucceeded,
  salesforceEditCardFailed,

  salesforceRemoveCard,
  salesforceRemoveCardSucceeded,
  salesforceRemoveCardFailed,

  salesforceAddCard,
  salesforceAddCardSucceeded,
  salesforceAddCardFailed,

  salesforceResetCard,
} = salesforceActions;

export type SalesCardState = {
  // This card_type needs to be string because the salesforce type differs
  card_type: string;
  hook: {
    headers: Record<string, string> | null;
    hook_id: string;
    title: string;
    uri_template: string;
    consumer_key: string;
    consumer_secret: string;
    security_token: string;
    account_email: string;
    account_password: string;
    created_at: string;
    card_version: string;
  };
  template: string;
};

export type SalesforceCardsState = {
  cards: SalesCardState[];
  currentCard: SalesCardState;
  testResult: TestResult | null;
  meta: Meta | null;
};

export function emptyCard(): SalesCardState {
  return {
    card_type: 'salesforceusernamepasswordflow',
    hook: {
      headers: null,
      hook_id: '',
      title: '',
      uri_template: '',
      consumer_key: '',
      consumer_secret: '',
      security_token: '',
      account_email: '',
      account_password: '',
      created_at: '',
      card_version: '',
    },
    template: '',
  } as SalesCardState;
}

function parseURLParameters(url: string): Record<string, string | null> {
  return {
    consumer_key: getParameterFromString('consumer_key', url),
    consumer_secret: getParameterFromString('consumer_secret', url),
    security_token: getParameterFromString('security_token', url),
    account_email: getParameterFromString('account_email', url),
    account_password: getParameterFromString('account_password', url),
  };
}

const initialState: SalesforceCardsState = {
  cards: [],
  currentCard: emptyCard(),
  testResult: null,
  meta: null,
};

// Reducer
export const salesforceCards = handleActions<SalesforceCardsState, any>(
  {
    [salesforceGetCards.toString()]: (state: SalesforceCardsState) => {
      return {
        ...state,
        meta: {
          ...state.meta,
          getCardsAsyncStatus: 'loading',
        },
      };
    },

    [salesforceGetCardsSucceeded.toString()]: (
      state: SalesforceCardsState,
      action: any,
    ) => {
      // http://proxy.dixa/v1/proxy/e7a04fc4-bba8-48a8-a92e-013606a188a6/salesforceusernamepasswordflow?
      // a_email={{a_email}}
      // &a_phone_number={{a_phone_number}}
      // &consumer_key=3MVG9fTLmJ60pJ5LxlND7lvp7lck6L42C33ZO_XHFDTXQa_TMZrtdv3qSAa2Vga9cpIt34PeBGhPhaeHD39Rt
      // &consumer_secret=3845306638882761522
      // &security_token=FHRo1q9WrceNIBTZEUhM42l4
      // &account_email=jvp@dixa.com
      // &account_password=Q7jDrgZANoUDJwLL
      const cards = action.payload.data.custom_cards.filter(
        (card: any) => card.card_type === 'salesforceusernamepasswordflow',
      );
      cards.forEach((card: any) => {
        card.hook = {
          ...card.hook,
          ...parseURLParameters(card.hook.uri_template),
        };
      });

      return {
        ...state,
        meta: {
          ...state.meta,
          getCardsAsyncStatus: 'succeeded',
        },
        cards: cards,
      };
    },

    [salesforceGetCardsFailed.toString()]: (state: SalesforceCardsState) => {
      return {
        ...state,
        meta: {
          ...state.meta,
          getCardsAsyncStatus: 'failed',
        },
      };
    },

    [salesforceGetCard.toString()]: (
      state: SalesforceCardsState,
      action: any,
    ) => {
      return {
        ...state,
        meta: { ...state.meta },
        currentCard: action.payload,
      };
    },

    [salesforceGetCardById.toString()]: (
      state: SalesforceCardsState,
      action: any,
    ) => {
      return {
        ...state,
        meta: {
          ...state.meta,
          getCardByIdAsyncStatus: 'loading',
        },
      };
    },

    [salesforceGetCardByIdSucceeded.toString()]: (
      state: SalesforceCardsState,
      action: any,
    ) => {
      const card = action.payload.data;
      card.hook = {
        ...card.hook,
        ...parseURLParameters(card.hook.uri_template),
      };

      return {
        ...state,
        currentCard: action.payload.data,
        meta: {
          ...state.meta,
          getCardByIdAsyncStatus: 'succeeded',
        },
      };
    },

    [salesforceGetCardByIdFailed.toString()]: (state: SalesforceCardsState) => {
      return {
        ...state,
        meta: {
          ...state.meta,
          getCardByIdAsyncStatus: 'failed',
        },
      };
    },

    [salesforceTestCard.toString()]: (state: SalesforceCardsState) => {
      return {
        ...state,
        meta: { ...state.meta, testAsyncStatus: 'loading' },
        testResult: null,
      };
    },

    [salesforceTestCardSucceeded.toString()]: (
      state: SalesforceCardsState,
      action: any,
    ) => {
      return {
        ...state,
        meta: { ...state.meta, testAsyncStatus: 'succeeded' },
        testResult: Object.assign({}, action.payload.data),
      };
    },

    [salesforceTestCardFailed.toString()]: (state: SalesforceCardsState) => {
      return { ...state, meta: { ...state.meta, testAsyncStatus: 'failed' } };
    },

    [salesforceAddCard.toString()]: (state: SalesforceCardsState) => {
      return {
        ...state,
        meta: {
          ...state.meta,
          createAsyncStatus: 'loading',
        },
      };
    },

    [salesforceAddCardSucceeded.toString()]: (state: SalesforceCardsState) => {
      return {
        ...state,
        meta: {
          ...state.meta,
          createAsyncStatus: 'succeeded',
        },
      };
    },

    [salesforceAddCardFailed.toString()]: (state: SalesforceCardsState) => {
      return {
        ...state,
        meta: {
          ...state.meta,
          createAsyncStatus: 'failed',
        },
      };
    },

    [salesforceRemoveCard.toString()]: (state: SalesforceCardsState) => {
      return {
        ...state,
        meta: {
          ...state.meta,
          removeAsyncStatus: 'loading',
        },
      };
    },

    [salesforceRemoveCardSucceeded.toString()]: (
      state: SalesforceCardsState,
      action: any,
    ) => {
      const cardsCopy = state.cards.slice();
      const cards = cardsCopy.filter(
        (x) => x.hook.hook_id !== action.payload.data.hook_id,
      );

      return {
        ...state,
        meta: {
          ...state.meta,
          removeAsyncStatus: 'succeeded',
        },
        currentCard: emptyCard(),
        cards: cards,
      };
    },

    [salesforceRemoveCardFailed.toString()]: (state: SalesforceCardsState) => {
      return {
        ...state,
        meta: {
          ...state.meta,
          removeAsyncStatus: 'failed',
        },
      };
    },

    [salesforceEditCard.toString()]: (state: SalesforceCardsState) => {
      return {
        ...state,
        meta: {
          ...state.meta,
          editAsyncStatus: 'loading',
        },
      };
    },

    [salesforceEditCardSucceeded.toString()]: (state: SalesforceCardsState) => {
      return {
        ...state,
        meta: {
          ...state.meta,
          editAsyncStatus: 'succeeded',
        },
      };
    },

    [salesforceEditCardFailed.toString()]: (state: SalesforceCardsState) => {
      return {
        ...state,
        meta: {
          ...state.meta,
          editAsyncStatus: 'failed',
        },
      };
    },

    [salesforceResetCard.toString()]: (state: SalesforceCardsState) => {
      return {
        ...state,
        currentCard: emptyCard(),
        testResult: null,
        meta: {
          ...state.meta,
          getCardByIdAsyncStatus: null,
        },
      };
    },
  },
  initialState,
);
